import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { processService } from '../../services/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

const CodeVisualizerTab = () => {
  const { projectId } = useParams();
  const [processes, setProcesses] = useState([]);
  const [files, setFiles] = useState([]);
  const [selectedProcessId, setSelectedProcessId] = useState('');
  const [selectedFileId, setSelectedFileId] = useState('');
  const [codeContent, setCodeContent] = useState(null);
  const [isLoadingProcesses, setIsLoadingProcesses] = useState(true);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [isLoadingCode, setIsLoadingCode] = useState(false);
  const [error, setError] = useState(null);
  const [processFilter, setProcessFilter] = useState('');
  const [fileFilter, setFileFilter] = useState('');
  const [showProcessDropdown, setShowProcessDropdown] = useState(false);
  const [showFileDropdown, setShowFileDropdown] = useState(false);
  const [isFullCodeExpanded, setIsFullCodeExpanded] = useState(true);
  const [isSimplifiedCodeExpanded, setIsSimplifiedCodeExpanded] = useState(true);

  // Load processes on mount
  useEffect(() => {
    if (projectId) {
      loadProcesses();
    }
  }, [projectId]);

  // Load files when process is selected
  useEffect(() => {
    if (selectedProcessId) {
      loadFiles(selectedProcessId);
      setSelectedFileId(''); // Reset file selection
      setCodeContent(null); // Clear code display
    } else {
      setFiles([]);
      setSelectedFileId('');
      setCodeContent(null);
    }
  }, [selectedProcessId]);

  // Load code when file is selected
  useEffect(() => {
    if (selectedFileId) {
      loadCodeContent(selectedFileId);
    } else {
      setCodeContent(null);
    }
  }, [selectedFileId]);

  const loadProcesses = async () => {
    setIsLoadingProcesses(true);
    setError(null);
    try {
      const response = await processService.getProcessesByProject(projectId);
      setProcesses(response.data || []);
    } catch (err) {
      console.error('Error loading processes:', err);
      setError('Failed to load processes');
    } finally {
      setIsLoadingProcesses(false);
    }
  };

  const loadFiles = async (processId) => {
    setIsLoadingFiles(true);
    setError(null);
    try {
      const response = await processService.getProcessFiles(processId);
      setFiles(response.data || []);
    } catch (err) {
      console.error('Error loading files:', err);
      setError('Failed to load files');
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const loadCodeContent = async (codeId) => {
    setIsLoadingCode(true);
    setError(null);
    try {
      const response = await processService.getCodeFile(codeId);
      setCodeContent(response.data);
    } catch (err) {
      console.error('Error loading code content:', err);
      setError('Failed to load code content');
    } finally {
      setIsLoadingCode(false);
    }
  };

  // Filter processes based on search input
  const filteredProcesses = processes.filter(process =>
    process.name.toLowerCase().includes(processFilter.toLowerCase()) ||
    (process.technicalName && process.technicalName.toLowerCase().includes(processFilter.toLowerCase()))
  );

  // Filter files based on search input
  const filteredFiles = files.filter(file =>
    file.filePath.toLowerCase().includes(fileFilter.toLowerCase())
  );

  // Custom markdown renderers for syntax highlighting
  const markdownComponents = {
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      return !inline && match ? (
        <SyntaxHighlighter
          style={vscDarkPlus}
          language={match[1]}
          PreTag="div"
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }
  };

  return (
    <div className="h-full flex flex-col bg-white rounded-lg shadow-sm overflow-hidden">
      {/* Dropdowns */}
      <div className="px-6 py-4 border-b border-gray-200 bg-white">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Process Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Process
            </label>
            {isLoadingProcesses ? (
              <div className="flex items-center justify-center py-3 px-4 border border-gray-300 rounded-md bg-gray-50">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-tqa-accent"></div>
                <span className="ml-2 text-sm text-gray-600">Loading processes...</span>
              </div>
            ) : processes.length === 0 ? (
              <div className="py-3 px-4 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-600">
                No processes found. Upload a UiPath project first.
              </div>
            ) : (
              <div className="relative">
                <input
                  type="text"
                  placeholder="Type to filter processes..."
                  value={processFilter}
                  onChange={(e) => {
                    setProcessFilter(e.target.value);
                    setShowProcessDropdown(true);
                  }}
                  onFocus={() => {
                    setProcessFilter(''); // Clear filter to show all processes
                    setShowProcessDropdown(true);
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      setShowProcessDropdown(false);
                      // Restore the selected process name if one is selected
                      if (selectedProcessId) {
                        const process = processes.find(p => p.id === selectedProcessId);
                        if (process) {
                          setProcessFilter(process.name);
                        }
                      }
                    }, 200);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-tqa-accent focus:border-transparent"
                />
                {showProcessDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    {filteredProcesses.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-600">No matching processes</div>
                    ) : (
                      filteredProcesses.map((process) => (
                        <div
                          key={process.id}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setSelectedProcessId(process.id);
                            setProcessFilter(process.name);
                            setShowProcessDropdown(false);
                          }}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        >
                          <div className="font-medium text-gray-900">{process.name}</div>
                          {process.technicalName && (
                            <div className="text-xs text-gray-500">{process.technicalName}</div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* File Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select File
            </label>
            {!selectedProcessId ? (
              <div className="py-3 px-4 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-600">
                Select a process first
              </div>
            ) : isLoadingFiles ? (
              <div className="flex items-center justify-center py-3 px-4 border border-gray-300 rounded-md bg-gray-50">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-tqa-accent"></div>
                <span className="ml-2 text-sm text-gray-600">Loading files...</span>
              </div>
            ) : files.length === 0 ? (
              <div className="py-3 px-4 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-600">
                No files found for this process
              </div>
            ) : (
              <div className="relative">
                <input
                  type="text"
                  placeholder="Type to filter files..."
                  value={fileFilter}
                  onChange={(e) => {
                    setFileFilter(e.target.value);
                    setShowFileDropdown(true);
                  }}
                  onFocus={() => {
                    setFileFilter(''); // Clear filter to show all files
                    setShowFileDropdown(true);
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      setShowFileDropdown(false);
                      // Restore the selected file name if one is selected
                      if (selectedFileId) {
                        const file = files.find(f => f.id === selectedFileId);
                        if (file) {
                          setFileFilter(file.filePath);
                        }
                      }
                    }, 200);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-tqa-accent focus:border-transparent"
                />
                {showFileDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    {filteredFiles.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-600">No matching files</div>
                    ) : (
                      filteredFiles.map((file) => (
                        <div
                          key={file.id}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setSelectedFileId(file.id);
                            setFileFilter(file.filePath);
                            setShowFileDropdown(false);
                          }}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        >
                          <div className="text-sm text-gray-900 truncate">{file.filePath}</div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
      </div>

      {/* Code Display */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {isLoadingCode ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tqa-accent mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading code...</p>
            </div>
          </div>
        ) : !codeContent ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-500">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                />
              </svg>
              <p className="mt-4 text-lg font-medium">No file selected</p>
              <p className="mt-2 text-sm">Select a process and file to view the code</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Code Content */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
              <div 
                className="bg-gray-800 px-4 py-2 flex items-center justify-between cursor-pointer hover:bg-gray-700 transition-colors"
                onClick={() => setIsFullCodeExpanded(!isFullCodeExpanded)}
              >
                <div className="flex items-center gap-2">
                  <svg 
                    className={`w-4 h-4 text-gray-300 transition-transform ${isFullCodeExpanded ? 'rotate-90' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span className="text-sm font-mono text-gray-300">Full Code View</span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(codeContent.code);
                  }}
                  className="text-xs px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
                  title="Copy to clipboard"
                >
                  Copy
                </button>
              </div>
              {isFullCodeExpanded && (
                <div className="max-h-[600px] overflow-auto bg-gray-900">
                  <pre className="p-4 text-sm text-gray-100 font-mono whitespace-pre-wrap">{codeContent.code}</pre>
                </div>
              )}
            </div>

            {/* Simplified Code (if available) */}
            {codeContent.simplifiedCode && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                <div 
                  className="bg-gray-800 px-4 py-2 flex items-center justify-between cursor-pointer hover:bg-gray-700 transition-colors"
                  onClick={() => setIsSimplifiedCodeExpanded(!isSimplifiedCodeExpanded)}
                >
                  <div className="flex items-center gap-2">
                    <svg 
                      className={`w-4 h-4 text-gray-300 transition-transform ${isSimplifiedCodeExpanded ? 'rotate-90' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="text-sm font-mono text-gray-300">Simplified View</span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(codeContent.simplifiedCode);
                    }}
                    className="text-xs px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
                    title="Copy to clipboard"
                  >
                    Copy
                  </button>
                </div>
                {isSimplifiedCodeExpanded && (
                  <div className="max-h-[600px] overflow-auto bg-gray-900">
                    <pre className="p-4 text-sm text-gray-100 font-mono whitespace-pre-wrap">{codeContent.simplifiedCode}</pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CodeVisualizerTab;
